import { useState } from "react";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full bg-transparent border-b hairline py-3 font-sans-ui text-sm tracking-wide focus:border-ink focus:outline-none";

const PasswordField = ({ label, value, onChange, testid, show, autoComplete }) => (
  <div>
    <label className="block font-sans-ui text-[10px] tracking-mega text-ink/50 mb-2">{label}</label>
    <input data-testid={testid} type={show ? "text" : "password"} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} className={input} />
  </div>
);

const strength = (pw) => {
  let n = 0;
  if (pw.length >= 10) n++;
  if (pw.length >= 14) n++;
  if (/[A-Za-z]/.test(pw) && /\d/.test(pw)) n++;
  if (/[^A-Za-z0-9]/.test(pw)) n++;
  return n;
};

export const ChangePasswordPanel = ({ token, onTokenRefresh }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) return setMsg({ ok: false, text: "Le due password non coincidono." });
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: { Authorization: `Bearer ${token}` } });
      onTokenRefresh(data.token);
      setCurrent(""); setNext(""); setConfirm("");
      setMsg({ ok: true, text: "Password aggiornata. Dal prossimo accesso usa la nuova password." });
    } catch (err) {
      const d = err.response?.data?.detail;
      setMsg({ ok: false, text: typeof d === "string" ? d : "Modifica non riuscita." });
    } finally {
      setSaving(false);
    }
  };

  const s = strength(next);
  return (
    <div className="max-w-xl bg-white border hairline p-8" data-testid="change-password-panel">
      <p className="font-sans-ui text-[10px] tracking-mega text-ink/40 mb-3">ACCOUNT</p>
      <h2 className="font-display text-3xl text-ink mb-2">Cambia password</h2>
      <p className="font-sans-ui text-[13px] text-ink/60 leading-relaxed mb-8">
        Minimo 10 caratteri con almeno una lettera e un numero. Le altre sessioni aperte verranno disconnesse.
      </p>
      <form onSubmit={submit} className="space-y-6">
        <PasswordField label="PASSWORD ATTUALE" value={current} onChange={setCurrent} testid="current-password-input" show={show} autoComplete="current-password" />
        <PasswordField label="NUOVA PASSWORD" value={next} onChange={setNext} testid="new-password-input" show={show} autoComplete="new-password" />
        {next && (
          <div className="flex gap-1" data-testid="password-strength">
            {[1, 2, 3, 4].map((i) => <span key={i} className={`h-1 flex-1 ${i <= s ? (s >= 3 ? "bg-emerald-500" : "bg-goldtan") : "bg-ink/10"}`} />)}
          </div>
        )}
        <PasswordField label="CONFERMA NUOVA PASSWORD" value={confirm} onChange={setConfirm} testid="confirm-password-input" show={show} autoComplete="new-password" />
        <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow((v) => !v)} className="inline-flex items-center gap-2 font-sans-ui text-[10px] tracking-mega text-ink/50 hover:text-ink transition-colors">
          {show ? <EyeOff size={13} /> : <Eye size={13} />} {show ? "NASCONDI" : "MOSTRA"} PASSWORD
        </button>
        {msg && <p data-testid="change-password-message" className={`font-sans-ui text-sm ${msg.ok ? "text-emerald-700" : "text-destructive"}`}>{msg.text}</p>}
        <button data-testid="change-password-submit" type="submit" disabled={saving || !current || !next || !confirm} className="bg-ink text-ivory px-6 py-3 text-[10px] tracking-mega font-semibold hover:bg-charcoal transition-colors disabled:opacity-50">
          {saving ? "SALVATAGGIO..." : "AGGIORNA PASSWORD"}
        </button>
      </form>
    </div>
  );
};
